<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("federation.invite_member") }}</template>
        <template v-slot:body>
            <div class="field" :class="{ 'has-error': !validEmail }">
                <label class="label">{{ translate("common.email") }}:</label>
                <div class="control">
                    <input class="input" v-model="email" type="email" maxlength="50" />
                    <p class="help" v-if="!validEmail">
                        {{ translate("flash_messages.user_email_valid") }}
                    </p>
                </div>
            </div>
            <div class="field">
                <label class="label">{{ translate("admin.manage_organizers.role") }}:</label>
                <div class="control">
                    <FederationRoleSelect v-model="role" />
                    <StaffBundlePicker v-if="role === FederationRoleEnum.Staff" v-model="bundles" />
                </div>
            </div>

            <RoleDifferences scope="federation" />
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="confirmInProgress" class="button is-primary" @click="confirmInvitation" :disabled="!canSubmit">
                    {{ translate("common.ok") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../../Common/RegularModal.vue";
    import { useModal } from "../../Common/useModal";
    import { translate } from "../../Common/translations";
    import { computed, ref } from "vue";
    import { isValidEmail } from "../../Common/email";
    import federationsApi, { FederationRole, FederationRoleEnum } from "../../Api/federationsApi";
    import { useFederationDashboardStore } from "./federationManagementStore";
    import { handleError } from "../../Common/errorHandler";
    import FederationRoleSelect from "./FederationRoleSelect.vue";
    import { successToast } from "../../Common/toasts";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import StaffBundlePicker from "../../Common/StaffBundlePicker.vue";
    import RoleDifferences from "../../Common/RoleDifferences.vue";
    import { Bundle } from "../../Common/consts";

    const store = useFederationDashboardStore();

    const { modal, open: openModal, close } = useModal();

    const email = ref("");
    const role = ref<FederationRole | null>(null);
    const bundles = ref<Bundle[]>([]);

    const validEmail = computed(() => {
        return isValidEmail(email.value) || email.value === "";
    });

    function open() {
        email.value = "";
        role.value = null;
        bundles.value = [];
        openModal();
    }

    const canSubmit = computed(() => {
        return (
            validEmail.value &&
            email.value.trim() !== "" &&
            role.value !== null &&
            (role.value !== FederationRoleEnum.Staff || bundles.value.length > 0)
        );
    });

    const confirmInProgress = ref(false);

    function confirmInvitation() {
        confirmInProgress.value = true;

        if (validEmail.value && email.value.trim() === "") {
            return;
        }
        if (role.value === null) {
            return;
        }

        return federationsApi
            .inviteMemberToFederation({
                federationId: store.federationId,
                email: email.value.trim(),
                role: role.value,
                bundles: bundles.value,
            })
            .then(() => {
                successToast(translate("federation.user_invited_successfully"));
                return store.refreshFederationMembers();
            })
            .then(() => {
                close();
            })
            .catch(handleError)
            .finally(() => {
                confirmInProgress.value = false;
            });
    }

    defineExpose({ open });
</script>

<style scoped></style>
